import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { BillingAddonType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getFormattedPrice } from 'webapp/utils/number';

import BillingQuantifiableAddonUpdateBaseComponent from 'webapp/components/billing/quantifiable-addon/update-base';
import BillingQuantifiableAddonUpdateWrapperComponent from 'webapp/components/billing/quantifiable-addon/update-wrapper';

import type BillingPlan from 'webapp/models-custom/billing-plan';

const MAX_PACKS = 200;

interface UpgradeSavings {
	planName: string;
	priceFormatted: string;
	savePct: number;
}

export default class BillingAiCreditsUpdateComponent extends BillingQuantifiableAddonUpdateBaseComponent {
	@service('navigation') declare navigationService: Service.Navigation;

	// current selection (what user will pay for next month and forward)
	initialQuantity = this.billingService.aiCreditPackItem?.quantity || 0;

	// granted amount for this period - e.g. user paid for 5 and changed to 4 in the same period - quantity is 4 but granted is still 5. So if they change from 4 to 6 still in the same month, they'll pay for 1 (6-5) instead of 2 (6-4)
	grantedQuantity = this.billingService.aiCreditPackItem?.grantedQuantity || 0;

	@tracked quantity = this.initialQuantity;

	addonType = BillingAddonType.AI_CREDITS;
	successMessage = 'AI credits updated!';

	openedEventName = TrackingEvent.ADD_CREDITS_POPUP_OPENED;
	completedEventName = TrackingEvent.ADD_CREDITS_POPUP_COMPLETED;

	buildOpenedProps(): APIData.Tracking.Data {
		return {
			plan: this.activePlan?.id,
			credits_balance: this.billingService.aiCreditsCurrent,
		};
	}

	buildCompletedProps(): APIData.Tracking.Data {
		return {
			old_packs: this.initialQuantity,
			new_packs: this.quantity,
			added_packs: this.quantity - this.initialQuantity,
			price: this.dueTodayCents,
		};
	}

	get creditsPerPack(): number {
		return this.billingService.aiCreditsInOnePack;
	}

	get totalCredits(): number {
		return this.quantity * this.creditsPerPack;
	}

	get totalCreditsFormatted(): string {
		return this.formatCount(this.totalCredits);
	}

	// total for all selected credits
	get totalPriceFormatted(): string {
		return getFormattedPrice(this.quantity * this.pricePerPackCents, 2);
	}

	// charged today: only what we are adding (0 when unchanged or reducing)
	get dueTodayCents(): number {
		const delta = this.quantity - this.grantedQuantity;
		return delta > 0 ? delta * this.pricePerPackCents : 0;
	}

	get dueTodayFormatted(): string {
		return getFormattedPrice(this.dueTodayCents, 2);
	}

	// first available upgrade plan on the same billing cycle whose AI credits
	// are cheaper per pack than ours (null when none is cheaper)
	get cheaperUpgradePlan(): BillingPlan | null {
		const billingCycle = this.activePlan?.billingCycle,
			ourPricePerPack = this.pricePerPackCents;

		const plan = this.billingService.availablePlans.find(
			(candidate) =>
				candidate.billingCycle === billingCycle &&
				this.billingService.isPlanUpgrade(candidate),
		);

		if (!plan) {
			return null;
		}

		const theirPricePerPack = plan.getAddonPrice(BillingAddonType.AI_CREDITS);
		if (theirPricePerPack === undefined || theirPricePerPack >= ourPricePerPack) {
			return null;
		}

		return plan;
	}

	get upgradeSavings(): UpgradeSavings | null {
		const plan = this.cheaperUpgradePlan;
		if (!plan) {
			return null;
		}

		const ourPricePerPack = this.pricePerPackCents,
			theirPricePerPack = plan.getAddonPrice(BillingAddonType.AI_CREDITS) ?? 0;

		// no actual saving when nothing is selected (e.g. 0 packs)
		if (this.quantity * (ourPricePerPack - theirPricePerPack) <= 0) {
			return null;
		}

		return {
			planName: plan.name,
			priceFormatted: getFormattedPrice(this.quantity * theirPricePerPack, 2),
			savePct: Math.round(((ourPricePerPack - theirPricePerPack) / ourPricePerPack) * 100),
		};
	}

	@action
	viewPlan(): void {
		this.args.onClose();
		this.navigationService.transitionToRoute('settings.billing.plans');
	}

	<template>
		<BillingQuantifiableAddonUpdateWrapperComponent
			@quantity={{this.quantity}}
			@max={{MAX_PACKS}}
			@confirmed={{this.confirmed}}
			@confirmName="confirmAiCredits"
			@confirmLabel="I confirm this purchase of extra AI credits and acknowledge Databox will invoice me as shown above."
			@finePrint="You're charged today. After that, it renews monthly and is billed separately from your plan, on your plan's billing date, until you adjust or cancel it."
			@isConfirmDisabled={{this.isConfirmDisabled}}
			@submitInProgress={{this.submitInProgress}}
			@onSliderChange={{this.onSliderChange}}
			@onConfirmChange={{this.onConfirmChange}}
			@onSave={{this.save}}
			@onClose={{@onClose}}
		>
			<:count>
				{{this.totalCreditsFormatted}}
				credits /month
			</:count>
			<:summary>
				<div class="quantifiable-addon__summary">
					<div class="quantifiable-addon__summary-title">Order summary</div>
					<div class="line">
						<span class="label">{{this.totalCreditsFormatted}} credits /month</span>
						<span class="value">{{this.totalPriceFormatted}}</span>
					</div>
					<div class="line line--total">
						<span class="label">Due today</span>
						<span class="value"><b>{{this.dueTodayFormatted}}</b></span>
					</div>
				</div>

				{{#if this.upgradeSavings}}
					<div class="quantifiable-addon__discount">
						<span>
							Save
							<b>{{this.upgradeSavings.savePct}}%</b>
							and pay
							<b>{{this.upgradeSavings.priceFormatted}}/mo</b>
							on
							{{this.upgradeSavings.planName}}.
						</span>
						<a role="button" {{on "click" this.viewPlan}}>See plan →</a>
					</div>
				{{/if}}
			</:summary>
		</BillingQuantifiableAddonUpdateWrapperComponent>
	</template>
}
